import {
  AGENT_COLORS,
  AGENT_DISPLAY_NAMES,
  AGENT_DESCRIPTIONS,
  AGENT_PRICE,
  BEHIND_THE_SCENES_AGENTS,
  type AgentId,
} from '../../theme'
import { pricing as pricingSot } from '../../config/pricing.generated'
import type { SkillStatusPill } from '../../roadmap/skillCardStatus'

/**
 * AI Workforce Org Chart — personalized team visualization.
 *
 * Used on:
 *   - Control Center dashboard (this React component)
 *   - Weekly Push Report (server-rendered HTML, see backend/functions/src/components/aiWorkforceOrgChartHtml.ts)
 *   - Blueprint results page (server-rendered HTML)
 *   - Onboarding success screen
 *
 * Layout: Ava (Workforce Manager) at the top, 8 front-line agents in a 4×2 grid below,
 * 5 behind-the-scenes agents as a footer band. Hired agents render in full color;
 * unhired agents are greyed with a "+ Hire — $X/mo" CTA.
 *
 * The data model is intentionally identical to the server-side HTML renderer
 * (`renderAiWorkforceOrgChartHtml`) — same input, same visual output, just
 * different rendering targets.
 *
 * Strategy reference: STRATEGY_REPORT.md → "AI Workforce Org Chart — Personalized
 * Team Visualization (Confirmed Mar 9, 2026)".
 */

// The front-line agents in canonical display order.
// Row 1: Google, Phone, Social, Web (presence)   Row 2: Ads, Video, Nurture (growth)
//
// 🔴 NO `story` ROW — AND THIS IS THE SECOND PLACE IT HAD TO BE REMOVED. Story was
// retired from the pricing SOT on 2026-08-18 (folded into the Video skill) and
// `Agents.tsx` dropped it then, with a comment saying so. This component kept its
// OWN hardcoded copy of the list, so the page that believed it had no story row
// rendered one anyway — "Story Skill · Video Interviews · + Add — $75/mo", offered
// to a live client (John spotted it in his own Control Center, 2026-09-18).
//
// ⚠️ THE $75 WAS A LITERAL FOR SOMETHING WITH NO PRICE. `story` is absent from
// `pricing.json` entirely, so that figure could not have come from the SOT — and
// `AgentKey`, which IS generated from it, already excludes story. Deriving the rows
// from the priced set is what makes this unable to come back: add a skill to the
// pricing SOT and it appears here; retire one and it leaves.
// [[feedback_two_lists_kept_in_agreement_by_hand]]
const PRICED: AgentId[] = (Object.keys(pricingSot.agents) as AgentId[])
const ROW_1: AgentId[] = ['google', 'phone', 'social', 'web'].filter((a) => PRICED.includes(a as AgentId)) as AgentId[]
const ROW_2: AgentId[] = ['ads', 'video', 'nurture', 'story'].filter((a) => PRICED.includes(a as AgentId)) as AgentId[]

// Emoji icons per agent (matches the prototype). These are visual-only —
// the canonical agent identity is the AgentId + name + accent color.
const AGENT_ICONS: Record<AgentId, string> = {
  google: '🌐',
  phone: '📞',
  social: '📲',
  web: '🌍',
  ads: '📈',
  video: '🎬',
  nurture: '💞',
  story: '🎤',
}

const BEHIND_THE_SCENES_ICONS: Record<string, string> = {
  it: '⚙️',
  security: '🔐',
  intelligence: '💡',
  optimization: '⚡',
  compliance: '📋',
}

// Short, scannable subtitles (matches the prototype's orgAgentSubtitle) — the
// full AGENT_DESCRIPTIONS sentence is too long for an org-chart card.
const AGENT_SUBTITLES: Record<AgentId, string> = {
  google: 'Search & Reviews',
  phone: 'Calls & Chat',
  social: 'Social Media',
  web: 'Website & Email',
  ads: 'Paid Advertising',
  video: 'YouTube & Video',
  nurture: 'Customer Relationships',
  story: 'Video Interviews',
}

export interface AiWorkforceOrgChartProps {
  /** Business name displayed in the header strip. */
  businessName: string
  /** Set of agent IDs the client has hired. Unhired agents render as grey + Hire CTA. */
  hiredAgents: Set<AgentId> | AgentId[]
  /** Optional callback when a non-hired agent is clicked (opens Hire modal). */
  onHireClick?: (agentId: AgentId) => void
  /** Optional click handler for hired agents (e.g., navigate to agent detail). */
  onAgentClick?: (agentId: AgentId) => void
  /** Compact variant: smaller cards, used in dashboard sidebars. */
  compact?: boolean
  /**
   * A hired skill's real status (audit E12, 2026-10-28) — from the setup card
   * (roadmap/skillCardStatus.ts). When given, a hired card shows THAT, and nothing when it
   * returns null; when absent (demo / showcase renders) the card keeps "Active 24/7".
   */
  statusFor?: (agentId: AgentId) => SkillStatusPill | null
}

export function AiWorkforceOrgChart({
  businessName,
  hiredAgents,
  onHireClick,
  onAgentClick,
  compact = false,
  statusFor,
}: AiWorkforceOrgChartProps) {
  const hiredSet = hiredAgents instanceof Set ? hiredAgents : new Set(hiredAgents)
  const isHired = (id: AgentId) => hiredSet.has(id)

  const cardSize = compact ? 'w-[110px]' : 'w-[130px] sm:w-[150px]'
  const iconSize = compact ? 'w-10 h-10 text-lg' : 'w-11 h-11 sm:w-14 sm:h-14 text-xl sm:text-2xl'
  const padding = compact ? 'p-3' : 'p-3 sm:p-4'

  return (
    <div className="ai-workforce-org-chart card rounded-2xl overflow-hidden shadow-sm" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      {/* Header strip */}
      <div className="bg-gradient-to-br from-blue-500 to-cyan-500 px-6 py-4">
        <div className="text-center">
          <div className="text-blue-100 text-xs font-medium tracking-wider uppercase mb-0.5">Ava &amp; Your Skills</div>
          <div className="text-white text-lg font-bold">{businessName}</div>
        </div>
      </div>

      <div className={compact ? 'p-4' : 'p-6 sm:p-8'}>
        {/* Ava — Workforce Manager */}
        <div className="flex flex-col items-center mb-2">
          <div className="relative rounded-full" style={{ animation: 'avaPulse 2s ease-in-out infinite' }}>
            <img
              src={`${import.meta.env.BASE_URL}Ava2.png`}
              alt="Ava"
              className={`${compact ? 'w-14 h-14' : 'w-16 h-16 sm:w-20 sm:h-20'} rounded-full object-cover shadow-lg`}
              style={{ border: '3px solid white' }}
              onError={(e) => {
                const img = e.target as HTMLImageElement
                img.style.display = 'none'
                const fallback = img.nextElementSibling as HTMLDivElement | null
                if (fallback) fallback.style.display = 'flex'
              }}
            />
            <div
              className={`${compact ? 'w-14 h-14' : 'w-16 h-16 sm:w-20 sm:h-20'} rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 items-center justify-center text-white text-2xl font-bold shadow-lg`}
              style={{ display: 'none', border: '3px solid white' }}
            >
              A
            </div>
            <div className={`absolute bottom-0 right-0 ${compact ? 'w-3.5 h-3.5' : 'w-4 h-4 sm:w-5 sm:h-5'} bg-emerald-400 rounded-full`} style={{ border: '2px solid white' }} />
          </div>
          <div className="mt-2 text-center">
            <div className={`font-bold ${compact ? 'text-sm' : 'text-sm sm:text-base'} theme-text-primary`}>Ava</div>
            <div className="text-xs theme-text-secondary">Your Assistant</div>
          </div>
        </div>

        {/* Connector trunk */}
        <div className="flex justify-center">
          <div className="w-px h-6" style={{ background: 'var(--border, #cbd5e1)' }} />
        </div>

        {/* Row 1: 4 agents */}
        <div className={`flex flex-wrap justify-center ${compact ? 'gap-3' : 'gap-4 sm:gap-6'} mb-4`}>
          {ROW_1.map((id) => (
            <AgentNode
              key={id}
              agentId={id}
              hired={isHired(id)}
              cardSize={cardSize}
              iconSize={iconSize}
              padding={padding}
              onHireClick={onHireClick}
              onAgentClick={onAgentClick}
              statusFor={statusFor}
            />
          ))}
        </div>

        {/* Row 2: 4 agents */}
        <div className={`flex flex-wrap justify-center ${compact ? 'gap-3' : 'gap-4 sm:gap-6'}`}>
          {ROW_2.map((id) => (
            <AgentNode
              key={id}
              agentId={id}
              hired={isHired(id)}
              cardSize={cardSize}
              iconSize={iconSize}
              padding={padding}
              onHireClick={onHireClick}
              onAgentClick={onAgentClick}
              statusFor={statusFor}
            />
          ))}
        </div>

        {/* Behind-the-scenes footer band */}
        <div className="mt-8 pt-5 border-t border-dashed" style={{ borderColor: 'var(--border, #cbd5e1)' }}>
          <div className="text-center mb-3">
            <div className="text-xs font-semibold uppercase tracking-wider theme-text-muted">Behind the Scenes</div>
            <div className="text-[10px] theme-text-muted mt-0.5">{BEHIND_THE_SCENES_AGENTS.length} agents included in your base platform — always active</div>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {BEHIND_THE_SCENES_AGENTS.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border theme-border theme-text-secondary text-[10px] sm:text-xs"
                style={{ backgroundColor: 'var(--bg-card)' }}
                title={agent.description}
              >
                <span className="text-sm">{BEHIND_THE_SCENES_ICONS[agent.id] ?? '⚙️'}</span>
                <span className="font-medium">{agent.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface AgentNodeProps {
  agentId: AgentId
  hired: boolean
  cardSize: string
  iconSize: string
  padding: string
  onHireClick?: (agentId: AgentId) => void
  onAgentClick?: (agentId: AgentId) => void
  statusFor?: (agentId: AgentId) => SkillStatusPill | null
}

const PILL_TONE: Record<SkillStatusPill['tone'], { pill: string; dot: string }> = {
  live: { pill: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300', dot: 'bg-emerald-400' },
  progress: { pill: 'bg-slate-100 dark:bg-slate-700 theme-text-secondary', dot: 'bg-slate-400' },
  'needs-you': { pill: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300', dot: 'bg-amber-400' },
}

function AgentNode({ agentId, hired, cardSize, iconSize, padding, onHireClick, onAgentClick, statusFor }: AgentNodeProps) {
  const colors = AGENT_COLORS[agentId]
  const displayName = AGENT_DISPLAY_NAMES[agentId]
  const description = AGENT_DESCRIPTIONS[agentId]
  const subtitle = AGENT_SUBTITLES[agentId]
  const icon = AGENT_ICONS[agentId]

  const handleClick = () => {
    if (hired && onAgentClick) {
      onAgentClick(agentId)
    }
  }

  const handleHire = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (onHireClick) onHireClick(agentId)
  }

  const cursor = hired && onAgentClick ? 'cursor-pointer' : ''
  const opacity = hired ? '' : 'opacity-70'
  const borderClass = hired ? `border-2 ${colors.border} shadow-md` : 'border-2 border-dashed border-slate-400'

  return (
    <div
      className={`agent-node rounded-xl ${borderClass} ${padding} text-center transition-all ${cardSize} ${cursor} ${opacity}`}
      style={{ background: 'var(--bg-card)' }}
      onClick={handleClick}
      title={description}
    >
      <div className={`${iconSize} rounded-full flex items-center justify-center mx-auto mb-2 shadow-sm ${hired ? colors.bg : 'bg-slate-100 dark:bg-slate-700'}`} style={{ border: '2px solid white' }}>
        <span>{icon}</span>
      </div>
      <div className="font-semibold text-xs sm:text-sm theme-text-primary">{displayName}</div>
      <div className="text-[10px] sm:text-xs theme-text-muted mt-0.5 leading-tight">{subtitle}</div>
      <div className="mt-2">
        {hired ? (() => {
          const pill: SkillStatusPill | null = statusFor ? statusFor(agentId) : { tone: 'live', label: 'Active 24/7' }
          if (!pill) return null
          const tone = PILL_TONE[pill.tone]
          return (
            <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${tone.pill} text-[10px] font-medium`}>
              <span className={`w-1.5 h-1.5 ${tone.dot} rounded-full`} />
              {pill.label}
            </div>
          )
        })() : (
          <button
            onClick={handleHire}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
          >
            + Add — ${AGENT_PRICE}/mo
          </button>
        )}
      </div>
    </div>
  )
}
